import React, { useState, useEffect, useRef, useMemo } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';

/**
 * Shared component for managing materials (carton or corrugated cardboard) in widget configuration.
 *
 * @param {string} materialType - The material type key (e.g., 'carton_material', 'corrugated_cardboard_material')
 * @param {string} translationPrefix - Translation key prefix (e.g., 'carton', 'corrugated_cardboard')
 * @param {function} onMaterialsChange - Callback when materials are added/removed
 */
const MaterialsSection = ({ materialType, translationPrefix, onMaterialsChange }) => {
  const [allMaterials, setAllMaterials] = useState([]);
  const [enabledMaterials, setEnabledMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [widgetLabel, setWidgetLabel] = useState('');
  const [formConfig, setFormConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [disabling, setDisabling] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [widgetMaterialId, setWidgetMaterialId] = useState('');

  const addPopupRef = useRef();
  const createPopupRef = useRef();
  const editPopupRef = useRef();

  // Translation helper with fallback
  const translate = (key, fallback) => {
    return t(`quote_builder_widget.${key}`) || fallback;
  };

  // Fetch all materials
  const fetchMaterials = () => {
    setLoading(true);
    setErrorMessage('');
    GetRequest({
      url: `/quote-builder/user_unit_prices/list/settings_list/${materialType}/${window.locale || 'en'}`,
      callback: (data) => {
        // The owner's own prices only: an admin's list also carries the system prices, which the widget never prices
        // with and which cannot be added to it.
        setAllMaterials(data.filter((material) => !material.is_system));
        const enabled = data.filter((material) => material.widget_enabled === 'true');
        setEnabledMaterials(enabled);
        setLoading(false);
      },
      500: () => {
        setLoading(false);
        setErrorMessage(translate('failed_to_load_materials', 'Failed to load materials'));
      }
    });
  };

  // Fetch form config for creating new materials
  const fetchFormConfig = () => {
    GetRequest({
      url: `/quote-builder/config/settings/user_unit_prices/${materialType}`,
      useCache: true,
      callback: (data) => {
        setFormConfig(data);
      },
      500: () => {
        setErrorMessage(translate('failed_to_load_form_config', 'Failed to load form configuration'));
      }
    });
  };

  useEffect(() => {
    fetchMaterials();
    fetchFormConfig();
  }, [materialType]);

  // Get material options from form config based on user's unit preference
  const materialOptions = useMemo(() => {
    if (!formConfig || !formConfig.attributes) {
      return [];
    }
    const userUnit = window.user_unit || 'mm';
    // There are two widget_material_id fields - one for mm and one for inches
    // Find the appropriate one based on user's unit preference
    const materialFields = formConfig.attributes.filter(attr => attr.name === 'widget_material_id');
    let materialField = null;

    if (materialFields.length > 1) {
      // Find the one that matches the current unit based on visible_if condition
      materialField = materialFields.find(field => {
        if (userUnit === 'mm' && field.visible_if && field.visible_if.includes("== 'mm'")) {
          return true;
        }
        if (userUnit !== 'mm' && field.visible_if && field.visible_if.includes("!= 'mm'")) {
          return true;
        }
        return false;
      }) || materialFields[0];
    } else {
      materialField = materialFields[0];
    }

    if (materialField && materialField.values) {
      return materialField.values.filter(v => v.value !== '');
    }
    return [];
  }, [formConfig]);

  // Enable material for widget
  const enableMaterial = (materialId, label, linkedMaterialId) => {
    setSaving(true);
    setErrorMessage('');
    const data = {
      widget_enabled: 'true',
      widget_label: label
    };
    if (linkedMaterialId) {
      data.widget_material_id = linkedMaterialId;
    }
    PostRequest({
      url: `/quote-builder/user_unit_prices/${materialId}/update-widget-status`,
      data: data,
      callback: () => {
        setSaving(false);
        setShowAddPopup(false);
        setSelectedMaterial(null);
        setWidgetLabel('');
        setWidgetMaterialId('');
        if (addPopupRef.current) {
          addPopupRef.current.style.display = 'none';
        }
        fetchMaterials();
        if (onMaterialsChange) {
          onMaterialsChange();
        }
      },
      400: (data) => {
        setSaving(false);
        setErrorMessage(data?.error || translate('failed_to_enable_material', 'Failed to enable material'));
      },
      404: () => {
        setSaving(false);
        setErrorMessage(translate('failed_to_enable_material', 'Failed to enable material'));
      },
      500: () => {
        setSaving(false);
        setErrorMessage(translate('server_error', 'Server error occurred'));
      }
    }).catch(() => {
      setSaving(false);
    });
  };

  // Disable material for widget
  const disableMaterial = (materialId) => {
    setDisabling(materialId);
    setErrorMessage('');
    PostRequest({
      url: `/quote-builder/user_unit_prices/${materialId}/update-widget-status`,
      data: {
        widget_enabled: 'false'
      },
      callback: () => {
        setDisabling(null);
        fetchMaterials();
        if (onMaterialsChange) {
          onMaterialsChange();
        }
      },
      400: (data) => {
        setDisabling(null);
        setErrorMessage(data?.error || translate('failed_to_disable_material', 'Failed to disable material'));
      },
      500: () => {
        setDisabling(null);
        setErrorMessage(translate('server_error', 'Server error occurred'));
      }
    });
  };

  // Handle material selection in popup
  const handleMaterialSelect = (material) => {
    if (material.widget_enabled === 'true') {
      setWidgetLabel(material.widget_label || '');
      setWidgetMaterialId(material.widget_material_id || '');
    } else {
      setWidgetLabel(material.widget_default_label || '');
      setWidgetMaterialId('');
    }
    setSelectedMaterial(material);
  };

  // The name shown in the widget starts as the chosen material's name ("SBS C1S 500gsm 0.675mm"), which the owner can
  // keep or change; a name they typed is never replaced by a later material choice.
  const materialOptionLabel = (materialId) => {
    const option = materialOptions.find((candidate) => String(candidate.value) === String(materialId));
    return option ? option.label : '';
  };

  const handleWidgetMaterialChange = (materialId) => {
    if (widgetLabel.trim() === '' || widgetLabel === materialOptionLabel(widgetMaterialId)) {
      setWidgetLabel(materialOptionLabel(materialId));
    }
    setWidgetMaterialId(materialId);
  };

  // Handle save in popup
  const handleSaveLabel = () => {
    if (!widgetLabel.trim()) {
      setErrorMessage(translate('widget_label_required', 'Widget label is required'));
      return;
    }
    if (materialOptions.length > 0 && !widgetMaterialId) {
      setErrorMessage(translate('widget_material_required', 'Please select a material'));
      return;
    }
    enableMaterial(selectedMaterial.id, widgetLabel.trim(), widgetMaterialId);
  };

  // Handle new material created
  const handleNewMaterialCreated = () => {
    setShowCreatePopup(false);
    if (createPopupRef.current) {
      createPopupRef.current.style.display = 'none';
    }
    fetchMaterials();
    if (onMaterialsChange) {
      onMaterialsChange();
    }
  };

  // Handle edit material
  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setShowEditPopup(true);
  };

  // Handle material edited via full form
  const handleMaterialEditComplete = () => {
    setShowEditPopup(false);
    setEditingMaterial(null);
    if (editPopupRef.current) {
      editPopupRef.current.style.display = 'none';
    }
    fetchMaterials();
    if (onMaterialsChange) {
      onMaterialsChange();
    }
  };

  // Render enabled materials list
  const renderEnabledMaterials = () => {
    if (enabledMaterials.length === 0) {
      return (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate(`no_${translationPrefix}_materials_enabled`, `No ${translationPrefix.replace('_', ' ')} materials enabled for widget`)}
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '10px' }}>
        {enabledMaterials.map((material) => (
          <div key={material.id} style={{
            marginBottom: '12px',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '12px 14px',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '2px' }}>
                  {material.widget_label}
                  {material.bobbin_enabled === true && (
                    <span
                      data-testid="widget-material-bobbin-badge"
                      style={{
                        marginLeft: '8px',
                        padding: '1px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        verticalAlign: 'middle',
                        backgroundColor: '#e9ecef',
                        color: '#495057'
                      }}
                    >
                      {t('quote_builder.bobbin_label')}
                    </span>
                  )}
                </div>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  {material.description}
                  {material.price_with_currency_formatted ? ` · ${material.price_with_currency_formatted}` : ''}
                  {material.widget_material_name ? ` · ${material.widget_material_name}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ color: '#0d6efd', padding: '2px 8px' }}
                  onClick={() => handleEditMaterial(material)}
                >
                  <i className="fa fa-pencil" aria-hidden="true"></i>
                  {translate('edit', 'Edit')}
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ color: '#dc3545', padding: '2px 8px' }}
                  onClick={() => disableMaterial(material.id)}
                  disabled={disabling === material.id}
                >
                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                  {disabling === material.id
                    ? translate('removing', 'Removing...')
                    : translate('remove', 'Remove')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  // Render add popup content
  const renderAddPopupContent = () => {
    const addTitle = translate(`add_${translationPrefix}_material`, `Add ${translationPrefix.replace('_', ' ')} Material`);
    const noMaterialsMessage = translate(`no_${translationPrefix}_materials`, `You have no ${translationPrefix.replace('_', ' ')} materials. Create one first.`);
    const createNewLabel = translate(`create_new_${translationPrefix}_material`, `Create New ${translationPrefix.replace('_', ' ')} Material`);

    return (
      <div style={{ padding: '20px', minWidth: '500px' }}>
        <h4>{addTitle}</h4>
        <hr />

        {allMaterials.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>{noMaterialsMessage}</p>
            <button
              type="button"
              className="btn simple-btn"
              onClick={() => {
                setShowAddPopup(false);
                if (addPopupRef.current) {
                  addPopupRef.current.style.display = 'none';
                }
                setShowCreatePopup(true);
              }}
            >
              <i className="fa fa-plus" aria-hidden="true"></i>{' '}
              {createNewLabel}
            </button>
          </div>
        ) : (
          <>
            {selectedMaterial ? (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>{translate('selected_material', 'Selected Material')}</strong>
                  <div style={{ padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '5px' }}>
                    {selectedMaterial.description}
                  </div>
                </div>

                {materialOptions.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <label><strong>{translate('widget_material', 'Material')}</strong></label>
                    <select
                      className="form-control"
                      value={widgetMaterialId}
                      onChange={(e) => handleWidgetMaterialChange(e.target.value)}
                      style={{ marginTop: '5px' }}
                    >
                      <option value="">{translate('select_material_option', '-- Select Material --')}</option>
                      {materialOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label><strong>{translate('widget_label', 'Name to show in widget')}</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    value={widgetLabel}
                    onChange={(e) => setWidgetLabel(e.target.value)}
                    placeholder={translate('enter_widget_label', 'Enter display name')}
                    style={{ marginTop: '5px' }}
                  />
                </div>

                {renderErrorMessage()}

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    className="btn simple-btn"
                    onClick={handleSaveLabel}
                    disabled={saving}
                  >
                    {saving ? translate('saving', 'Saving...') : translate('save', 'Save')}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSelectedMaterial(null);
                      setWidgetLabel('');
                      setWidgetMaterialId('');
                      setErrorMessage('');
                    }}
                  >
                    {translate('back', 'Back')}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>{translate('select_material', 'Select a material to enable')}</strong>
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {allMaterials.map((material) => {
                    const isEnabled = material.widget_enabled === 'true';
                    return (
                      <div
                        key={material.id}
                        onClick={() => handleMaterialSelect(material)}
                        style={{
                          padding: '10px 15px',
                          borderRadius: '4px',
                          marginBottom: '5px',
                          cursor: 'pointer',
                          backgroundColor: isEnabled ? '#d4edda' : '#f8f9fa',
                          border: isEnabled ? '1px solid #c3e6cb' : '1px solid #dee2e6',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div>{material.description}</div>
                          {material.price_with_currency_formatted && (
                            <small style={{ color: '#666' }}>{material.price_with_currency_formatted}</small>
                          )}
                        </div>
                        {isEnabled && (
                          <span style={{ color: '#28a745' }}>
                            <i className="fa fa-check-circle" aria-hidden="true"></i>
                            {' '}{translate('enabled', 'Enabled')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <hr />
                <button
                  type="button"
                  className="btn simple-btn"
                  onClick={() => {
                    setShowAddPopup(false);
                    if (addPopupRef.current) {
                      addPopupRef.current.style.display = 'none';
                    }
                    setShowCreatePopup(true);
                  }}
                >
                  <i className="fa fa-plus" aria-hidden="true"></i>{' '}
                  {createNewLabel}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render create popup
  const renderCreatePopup = () => {
    if (!formConfig) {
      return null;
    }

    return (
      <ContentPopup
        myRef={createPopupRef}
        autoOpen={true}
        content={
          <QuoteBuilderForm
            config={formConfig}
            showingState="new"
            afterSave={handleNewMaterialCreated}
            afterCancel={handleNewMaterialCreated}
            context={{ widget_enabled: 'true' }}
          />
        }
        cancelButton={false}
        beforeClose={handleNewMaterialCreated}
      />
    );
  };

  // Render edit popup
  const renderEditPopup = () => {
    if (!formConfig || !editingMaterial) {
      return null;
    }

    return (
      <ContentPopup
        myRef={editPopupRef}
        autoOpen={true}
        content={
          <QuoteBuilderForm
            config={formConfig}
            showingState="edit"
            editStateId={editingMaterial.id}
            afterSave={handleMaterialEditComplete}
            afterCancel={handleMaterialEditComplete}
            context={{ widget_enabled: 'true' }}
          />
        }
        cancelButton={false}
        beforeClose={handleMaterialEditComplete}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '10px', color: '#666' }}>
        {translate('loading', 'Loading...')}
      </div>
    );
  }

  const addButtonLabel = translate(`add_${translationPrefix}_material`, `Add ${translationPrefix.replace('_', ' ')} Material`);

  return (
    <div>
      {!showAddPopup && renderErrorMessage()}

      {renderEnabledMaterials()}

      <button
        type="button"
        className="btn simple-btn"
        onClick={() => setShowAddPopup(true)}
      >
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {addButtonLabel}
      </button>

      {showAddPopup && (
        <ContentPopup
          myRef={addPopupRef}
          autoOpen={true}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowAddPopup(false);
            setSelectedMaterial(null);
            setWidgetLabel('');
            setWidgetMaterialId('');
            setErrorMessage('');
          }}
        />
      )}

      {showCreatePopup && renderCreatePopup()}

      {showEditPopup && renderEditPopup()}
    </div>
  );
};

export default MaterialsSection;
